import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as fbSignOut,
  User
} from 'firebase/auth';
import store, {
  parseSessionTokenClaims,
  saveCachedAuthState,
  clearCachedAuthState,
  getActiveSessionToken
} from '../state';

const DEFAULT_GATEKEEPER_API_BASE = 'https://auth.skyskout.com';

export const firebaseConfig = {
  apiKey: "AIzaSyDO5BmXgk75LRcWnUcp0EP4h2GnkmazBwA",
  authDomain: "skyskout-ec4b1.firebaseapp.com",
  projectId: "skyskout-ec4b1",
  storageBucket: "skyskout-ec4b1.firebasestorage.app",
  messagingSenderId: "312730757525",
  appId: "1:312730757525:web:a8b065367c73f7a05d6e9c",
  measurementId: "G-7KKX7KD26C"
};

class AuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private isInitialized = false;
  private inFlightSessionRefresh: Promise<boolean> | null = null;
  private inFlightRefreshUid: string | null = null;
  private lastVerifiedUid: string | null = null;
  private lastVerifiedApiBase: string | null = null;

  constructor() {
    this.app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    this.auth = getAuth(this.app);
    this.initListener();
  }

  private initListener(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    onAuthStateChanged(this.auth, async (fbUser: User | null) => {
      if (!store || typeof store.getState !== 'function') {
        await new Promise<void>(r => queueMicrotask(r));
      }
      if (fbUser) {
        const userInfo = {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName || fbUser.email?.split('@')[0] || 'Explorer',
          photoURL: fbUser.photoURL
        };

        const currentState = store.getState();
        const existingClaims = parseSessionTokenClaims(currentState.sessionToken);
        const isMatchingCachedSession = existingClaims && existingClaims.uid === fbUser.uid;

        if (!isMatchingCachedSession && currentState.sessionToken) {
          clearCachedAuthState();
        }

        store.update({
          user: userInfo,
          isAuthenticated: true,
          isAuthLoading: true,
          ...(isMatchingCachedSession
            ? {}
            : {
                sessionToken: null,
                subscription: {
                  hasAccess: false,
                  status: 'none',
                  plan: null,
                  currentPeriodEnd: null,
                  trialEnds: null,
                  tier: 'registered'
                }
              })
        });

        // Exchange Firebase ID Token for edge session cookie and verified tier
        try {
          await this.refreshSessionCookie(fbUser);
        } catch (err) {
          console.warn('⚠️ Session cookie exchange failed:', err);
        } finally {
          store.update({ isAuthLoading: false });
        }
      } else {
        this.lastVerifiedUid = null;
        this.lastVerifiedApiBase = null;
        clearCachedAuthState();
        store.update({
          sessionToken: null,
          user: null,
          isAuthenticated: false,
          isAuthLoading: false,
          subscription: {
            hasAccess: false,
            status: 'none',
            plan: null,
            currentPeriodEnd: null,
            trialEnds: null,
            tier: 'free'
          }
        });
        this.invalidateTileCachesOnSessionChange();
      }
    });

    // If manifest loads after onAuthStateChanged started (or updates api_server),
    // ensure any unverified or mismatched session is immediately refreshed against the manifest's api_server.
    const bindManifestObserver = () => {
      if (!store || typeof store.getState !== 'function') return;
      let lastObservedManifestApi: string | null = store.getState().manifest?.api_server || null;
      store.subscribe((state) => {
        const manifestApi = state.manifest?.api_server || null;
        if (manifestApi && manifestApi !== lastObservedManifestApi) {
          lastObservedManifestApi = manifestApi;
          const currentUser = this.auth.currentUser;
          const resolvedApi = this.getApiBase();
          if (
            currentUser &&
            !this.inFlightSessionRefresh &&
            (this.lastVerifiedUid !== currentUser.uid || this.lastVerifiedApiBase !== resolvedApi)
          ) {
            this.refreshSessionCookie(currentUser).catch(() => {});
          }
        }
      });
    };

    if (store && typeof store.getState === 'function') {
      bindManifestObserver();
    } else {
      queueMicrotask(bindManifestObserver);
    }
  }

  public getApiBase(): string {
    const manifest = (store && typeof store.getState === 'function') ? store.getState().manifest : null;
    if (manifest && manifest.api_server) {
      return manifest.api_server.replace(/\/+$/, '');
    }
    if (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.api_server) {
      return String(__APP_CONFIG__.api_server).replace(/\/+$/, '');
    }
    return DEFAULT_GATEKEEPER_API_BASE;
  }

  public isRefreshingSession(): boolean {
    return this.inFlightSessionRefresh !== null || Boolean(store && typeof store.getState === 'function' && store.getState().isAuthLoading);
  }

  public async waitForSessionRefresh(): Promise<boolean> {
    if (this.inFlightSessionRefresh) {
      return this.inFlightSessionRefresh;
    }
    const currentUser = this.auth.currentUser;
    if (currentUser && this.lastVerifiedUid !== currentUser.uid) {
      return this.refreshSessionCookie(currentUser);
    }
    return Boolean(store && typeof store.getState === 'function' && store.getState().subscription?.hasAccess);
  }

  public async refreshSessionCookie(fbUser?: User | null, sessionId?: string | null): Promise<boolean> {
    const currentUser = fbUser || this.auth.currentUser;
    if (!currentUser) return false;

    if (!sessionId && this.inFlightSessionRefresh && this.inFlightRefreshUid === currentUser.uid) {
      return this.inFlightSessionRefresh;
    }

    const promise = this.performSessionRefresh(currentUser, sessionId);
    if (!sessionId) {
      this.inFlightSessionRefresh = promise;
      this.inFlightRefreshUid = currentUser.uid;
      promise.finally(() => {
        if (this.inFlightSessionRefresh === promise) {
          this.inFlightSessionRefresh = null;
          this.inFlightRefreshUid = null;
        }
      });
    }
    return promise;
  }

  private async performSessionRefresh(currentUser: User, sessionId?: string | null): Promise<boolean> {
    let forceTokenRefresh = false;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        let idToken: string;
        try {
          idToken = await currentUser.getIdToken(forceTokenRefresh || attempt > 0);
        } catch (_) {
          idToken = await currentUser.getIdToken(true);
        }

        const apiBase = this.getApiBase();
        const res = await fetch(`${apiBase}/auth/session`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ sessionId: sessionId || undefined })
        });

        if (res.status === 401 && !forceTokenRefresh && attempt === 0) {
          forceTokenRefresh = true;
          continue;
        }

        if (!res.ok) {
          console.warn(`[AuthService] /auth/session returned ${res.status} (attempt ${attempt + 1}/3)`);
          if (attempt < 2 && (res.status >= 500 || res.status === 429 || res.status === 404 || res.status === 405)) {
            await new Promise(r => setTimeout(r, 600 * (attempt + 1)));
            continue;
          }
          return false;
        }

        const data = await res.json();
        const sessionToken = data.sessionToken || null;

        const resolvedTier = (data.tier || data.subscription?.tier || (data.subscription?.hasAccess ? 'pro' : 'registered')) as 'free' | 'registered' | 'pro' | 'ultra';
        const hasAccess = Boolean(
          data.subscription?.hasAccess ||
          resolvedTier === 'pro' ||
          resolvedTier === 'ultra'
        );

        const updatedSub = {
          ...(store.getState().subscription || {
            hasAccess: false,
            status: 'none' as const,
            plan: null,
            currentPeriodEnd: null,
            trialEnds: null,
          }),
          ...(data.subscription || {}),
          hasAccess,
          tier: resolvedTier,
        };

        const userInfo = {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Explorer',
          photoURL: currentUser.photoURL
        };

        saveCachedAuthState({
          uid: currentUser.uid,
          user: userInfo,
          sessionToken,
          subscription: updatedSub,
          updatedAt: Date.now()
        });

        this.lastVerifiedUid = currentUser.uid;
        this.lastVerifiedApiBase = apiBase;

        store.update({
          user: userInfo,
          isAuthenticated: true,
          isAuthLoading: false,
          sessionToken,
          subscription: updatedSub
        });

        this.invalidateTileCachesOnSessionChange();

        console.log('[AuthService] Edge session verified, tier:', resolvedTier, 'subscription status:', updatedSub.status, 'token present:', !!sessionToken);
        return true;
      } catch (e) {
        console.warn(`[AuthService] Could not reach /auth/session endpoint (attempt ${attempt + 1}/3):`, e);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 600 * (attempt + 1)));
          continue;
        }
        return false;
      }
    }

    return false;
  }

  public async createCheckoutSession(plan: '1_month' | '6_month' | '12_month'): Promise<{ success: boolean; error?: string }> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'Please sign in to choose a subscription plan.' };
    }

    try {
      const idToken = await currentUser.getIdToken(true);
      const apiBase = this.getApiBase();
      const res = await fetch(`${apiBase}/auth/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ plan })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Checkout failed (${res.status})`);
      }

      const data = await res.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return { success: true };
      }
      throw new Error('No checkout URL returned from server.');
    } catch (err: any) {
      console.error('[AuthService] Checkout error:', err);
      return { success: false, error: err.message || 'Failed to initialize checkout.' };
    }
  }

  public async openBillingPortal(): Promise<{ success: boolean; error?: string }> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'Please sign in to manage billing.' };
    }

    try {
      const idToken = await currentUser.getIdToken(true);
      const apiBase = this.getApiBase();
      const res = await fetch(`${apiBase}/auth/billing-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Customer portal error (${res.status})`);
      }

      const data = await res.json();
      if (data.portalUrl) {
        window.location.href = data.portalUrl;
        return { success: true };
      }
      throw new Error('No customer portal URL returned from server.');
    } catch (err: any) {
      console.error('[AuthService] Billing portal error:', err);
      return { success: false, error: err.message || 'Failed to open customer portal.' };
    }
  }

  public async signInWithEmail(email: string, pass: string): Promise<User> {
    const cred = await signInWithEmailAndPassword(this.auth, email.trim(), pass);
    await this.refreshSessionCookie(cred.user);
    return cred.user;
  }

  public async registerWithEmail(email: string, pass: string): Promise<User> {
    const cred = await createUserWithEmailAndPassword(this.auth, email.trim(), pass);
    await this.refreshSessionCookie(cred.user);
    return cred.user;
  }

  public async signInWithGoogle(): Promise<User> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const cred = await signInWithPopup(this.auth, provider);
    await this.refreshSessionCookie(cred.user);
    return cred.user;
  }

  public async signInWithApple(): Promise<User> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const cred = await signInWithPopup(this.auth, provider);
    await this.refreshSessionCookie(cred.user);
    return cred.user;
  }

  public async signOut(): Promise<void> {
    this.lastVerifiedUid = null;
    this.lastVerifiedApiBase = null;
    clearCachedAuthState();
    await fbSignOut(this.auth);
    try {
      const apiBase = this.getApiBase();
      await fetch(`${apiBase}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (_) {}

    clearCachedAuthState();

    store.update({
      sessionToken: null,
      user: null,
      isAuthenticated: false,
      isAuthLoading: false,
      isAuthModalOpen: false,
      isProfileModalOpen: false,
      subscription: {
        hasAccess: false,
        status: 'none',
        plan: null,
        currentPeriodEnd: null,
        trialEnds: null,
        tier: 'free'
      }
    });
    this.invalidateTileCachesOnSessionChange();
  }

  private invalidateTileCachesOnSessionChange(): void {
    Promise.all([
      import('../config').then(m => m.clearPmtilesCache()).catch(() => {}),
      import('./ForecastBinaryService').then(m => m.ForecastBinaryService.clearCache()).catch(() => {}),
      import('./ObservedBinaryService').then(m => m.ObservedBinaryService.clearCache()).catch(() => {}),
      import('./ClimateService').then(m => m.ClimateService.clearCache()).catch(() => {}),
      import('./RecentClimateService').then(m => m.RecentClimateService.clearCache()).catch(() => {}),
      import('./ModelComparisonChartService').then(m => {
        if (typeof (m.ModelComparisonChartService as any)?.clearCache === 'function') {
          (m.ModelComparisonChartService as any).clearCache();
        }
      }).catch(() => {}),
      import('./LegendTrendChartService').then(m => {
        const svc = m.LegendTrendChartService as any;
        if (typeof svc?.clearCaches === 'function') {
          svc.clearCaches();
        }
        if (typeof svc?.updateCharts === 'function') {
          svc.updateCharts(store.getState());
        }
      }).catch(() => {}),
    ]).catch(() => {});
  }

  public getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  public getSessionToken(): string | null {
    return getActiveSessionToken();
  }
}

export const authService = new AuthService();
export default authService;
